/**
 * Live Ball app-level constants — API endpoints, default credentials, timings.
 *
 * Endpoints / signing rules mirror the AMG single-player conventions:
 *   - POST /action/login    (no signing — username/password)
 *   - POST /action/opengame (md5 = MD5(agentId + gameKey + userName))
 *   - POST /action/balance  (same md5 as opengame)
 *   - POST /action/getinfo  (md5 = MD5(userName))
 *   - POST /action/bet      (same md5 as opengame; betDetail = BetLine[])
 *
 * Both legacy `{ ErrorCode, Message, Data }` and the newer
 * `{ success, error, message, data }` envelopes are accepted.
 */

export const API_BASE = 'https://staginggameapi.tbgcablecar.xyz/api';
export const HUB_ORIGIN = 'https://staginggameapi.tbgcablecar.xyz';

export const Endpoints = {
  login: `${API_BASE}/action/login`,
  openGame: `${API_BASE}/action/opengame`,
  checkBalance: `${API_BASE}/action/balance`,
  getInfo: `${API_BASE}/action/getinfo`,
  placeBet: `${API_BASE}/action/bet`,
  getHistory: `${API_BASE}/action/bethistory`,
} as const;

/**
 * Multiplayer SignalR hub URLs per variant. The dragontiger / baccarat hubs
 * already live on staging at `<origin>/dragontigerhub` and `<origin>/baccarathub`
 * — the Live Ball hubs follow the same naming. At the time of writing the four
 * BAL hubs return 404 on staging (backend not deployed yet); these URLs work
 * the moment the server team brings them up.
 */
export const HUB_URLS = {
  BAL6: `${HUB_ORIGIN}/ball6hub`,
  BAL12: `${HUB_ORIGIN}/ball12hub`,
  BAL24: `${HUB_ORIGIN}/ball24hub`,
  BAL48: `${HUB_ORIGIN}/ball48hub`,
} as const;

/**
 * Seconds shaved off the StartBet → EndBet window so clients close their UI
 * just before the server rejects late bets. Matches GameConstants.betCountdownOffset
 * in amgcommon_v2 (the dragontiger reference uses 2 seconds).
 */
export const BET_COUNTDOWN_OFFSET_SEC = 2;

export const DEFAULT_AGENT_ID = 'Agent1';

/** Dev-only fallback credentials when the page is opened without ?token=&gsid=. */
export const DEFAULT_USER_NAME = 'user2';
export const DEFAULT_PASSWORD = '123456';

/**
 * Default game config — used only as a stop-gap until /action/getinfo
 * (or /action/opengame) replies with the real values. The live BAL6 endpoint
 * returns `Chips: "50,100,200,500,800,1000"` with `MinBet:50, MaxBet:1000`,
 * so these defaults mirror that.
 */
export const DEFAULT_GAME_CONFIG = {
  minBet: 50,
  maxBet: 1000,
  chipValues: [50, 100, 200, 500, 800, 1000] as const,
};

/** Round-flow timings (ms). */
export const BETTING_DURATION_MS = 30_000;       // Player has 30s to place bets.
export const DRAW_DURATION_MS = 4_000;           // Spin / animation length.
export const RESULT_HOLD_MS = 5_000;             // How long to show win/lose toast.
export const NEW_ROUND_DELAY_MS = 1_500;         // Between rounds.
export const COUNTDOWN_DANGER_THRESHOLD_S = 5;   // Last 5 seconds flash red.
